import React, { useCallback, useMemo } from 'react';
import { Site, getSiteTags, TagDefinition } from '../../../models';
import getViewsData from './viewsData';
import { useSharedStyles, ExpandableListGroup, TagList, FilterableTagList } from './common';
import { Card } from 'src/components/ui/card';
import { Theme } from '@material-ui/core';
import { makeStyles } from '@material-ui/core';
import { createStyles } from '@material-ui/core';
import { Text } from 'src/components/ui/typography';
import { View } from 'src/state/contexts/analysisPage';

export interface ViewTreeProps {
  site: Site;
  onTagSelect: (tagId: string) => void;
  selectedTags: string[];
  tagFilter?: (tag: TagDefinition) => boolean;
  height?: number | string;
  style?: React.CSSProperties;
  disabled?: (tag: TagDefinition) => boolean;
  selectedViews: View[];
  setSelectedViews: (views: View[]) => void;
  clearAll?: () => void;
  hideSelectedWhenEmpty?: boolean;
  tabs?: React.ReactNode;
}

const useStyles = makeStyles<Theme>((theme: Theme) =>
  createStyles({
    tagListContainer: {
      padding: '4px 14px 4px 10px',
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
    },
    cardTitle: {
      padding: '4px 8px',
    },
  })
);
export const ViewTree: React.FC<ViewTreeProps> = (props) => {
  const classes = useStyles();
  const { site, selectedTags, disabled, tagFilter, selectedViews, setSelectedViews, tabs } = props;
  const sharedClasses = useSharedStyles({ height: props.height });

  const allTags = useMemo(() => {
    if (tagFilter) {
      return getSiteTags(site).filter(tagFilter);
    } else {
      return getSiteTags(site);
    }
  }, [site, tagFilter]);

  const viewsData = useMemo(() => {
    return getViewsData(allTags);
  }, [allTags]);

  const onCheckboxToggle = useCallback(
    (
      id: string,
      label: string,
      filteredViewsCharts: { id: string; label: string; tags: TagDefinition[] }[]
    ) => {
      if (selectedViews.some((view) => view.id === id)) {
        setSelectedViews(selectedViews.filter((view) => view.id !== id));
      } else {
        // transform filteredViewsCharts to Chart[]
        const filteredViewsChartsWithTagsIds = filteredViewsCharts.map((chart) => ({
          id: chart.id,
          label: chart.label,
          tagsIds: chart.tags.map((tag) => tag.id),
        }));
        setSelectedViews([...selectedViews, { id, label, charts: filteredViewsChartsWithTagsIds }]);
      }
    },
    [selectedViews, setSelectedViews]
  );
  const filterTags = (tag: TagDefinition, filterText: string): boolean => {
    const textFilter = tag.label.toLowerCase().includes(filterText.toLowerCase());
    const topFilter = props.tagFilter ? props.tagFilter(tag) : true;
    return textFilter && topFilter;
  };

  const renderAvailableTags = (filteredTags: TagDefinition[]) => {
    // If we have filtered tags, we need to filter our views to only include those with matching tags
    const relevantViews = viewsData.filter((view) =>
      view.charts.some((chart) => chart.tags.some((tag) => filteredTags.includes(tag)))
    );

    // Use all views if no filtering is applied
    const viewsToRender = filteredTags.length === allTags.length ? viewsData : relevantViews;

    return (
      <>
        {viewsToRender.map(({ id, label, charts }) => {
          // For each view, only show tags that match the filter
          const filteredViewsCharts =
            filteredTags.length === allTags.length
              ? charts
              : charts.filter((chart) => chart.tags.some((tag) => filteredTags.includes(tag)));

          // Only render views that have at least one tag after filtering
          return filteredViewsCharts.length > 0 ? (
            <ExpandableListGroup
              key={id}
              id={id}
              label={label}
              withCheckbox={true}
              isSelected={selectedViews.some((view) => view.id === id)}
              onCheckboxToggle={() => onCheckboxToggle(id, label, filteredViewsCharts)}
            >
              <div className={classes.tagListContainer}>
                {filteredViewsCharts.map(({ id, label, tags }) => (
                  <Card key={id}>
                    <Text className={classes.cardTitle}>{label}</Text>
                    <TagList
                      tags={tags}
                      onToggle={props.onTagSelect}
                      selectedTags={selectedTags}
                      disabled={disabled}
                      showCheckbox={false}
                    />
                  </Card>
                ))}
              </div>
            </ExpandableListGroup>
          ) : null;
        })}
      </>
    );
  };

  return (
    <div className={sharedClasses.mainContainer}>
      <div className={sharedClasses.container}>
        <FilterableTagList
          tabs={tabs}
          tags={allTags}
          renderTags={renderAvailableTags}
          headerTitle={`Available: ${allTags.length}`}
          filterCallback={filterTags}
        />
      </div>
    </div>
  );
};
